import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import * as React from 'react';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { commonMessages } from 'src/utils/messages';
import { booleanBadgeOptions } from 'src/utils/constants';
import { ServicePlan } from 'src/features/servicePlans/utils/types';
import { useListServicePlans } from 'src/features/servicePlans/api/useListServicePlans';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import CreateServicePlanFormModal from 'src/features/servicePlans/components/forms/CreateServicePlanFormModal';
import CellServicePlanActions from 'src/features/servicePlans/components/tables/CellServicePlanActions';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

const ServicePlansTable = () => {
    const { formatMessage } = useIntl();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(servicePlansMessages.servicePlans));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: servicePlans,
        isLoading,
        isError,
    } = useListServicePlans({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<ServicePlan>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/service-plans/${ctx.row.original.id}`}
                    render={(name, row) => (
                        <div>
                            <div className="text-sm font-medium">{name}</div>
                            <div className="text-sm text-gray-600">{row.code}</div>
                        </div>
                    )}
                />
            ),
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('category.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(servicePlansMessages.category),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(servicePlansMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('external_service_plan_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(servicePlansMessages.externalServicePlan),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellServicePlanActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<ServicePlan, unknown>
            columns={columns}
            data={servicePlans}
            semantics={{
                title: formatMessage(servicePlansMessages.servicePlans),
                entryName: formatMessage(servicePlansMessages.plan).toLowerCase(),
                entryNames: formatMessage(servicePlansMessages.plans).toLowerCase(),
                placeholder: formatMessage(servicePlansMessages.plan).toLowerCase(),
            }}
            search={search}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateServicePlanFormModal }),
            }}
        />
    );
};

export default ServicePlansTable;
